import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../components/FormField.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type {
  PlatformWebSearchKeySummary,
  TenantWebSearchBackendSummary,
  ToastMessage,
} from '../../types.js';

/** Owns the platform-global Gemini key and the selected tenant's grounding
 *  configuration while presenting their different scopes as separate panels. */
export function GeminiSearchSettings({
  http,
  tenantId,
  webSearchBackends,
  busy,
  setBusy,
  onToast,
  onChanged,
}: {
  http: AdminHttpClient;
  tenantId: string;
  webSearchBackends: TenantWebSearchBackendSummary[];
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onToast: (toast: ToastMessage) => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const translate = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      String(options ? t(key, options) : t(key)),
    [t]
  );
  const [platformSearchKey, setPlatformSearchKey] = useState<PlatformWebSearchKeySummary>({
    provider: 'gemini',
    hasCredential: false,
  });
  const [searchApiKey, setSearchApiKey] = useState('');
  const [maxUses, setMaxUses] = useState('5');

  const geminiSearchBackend = webSearchBackends.find((backend) => backend.provider === 'gemini');

  useEffect(() => {
    const backend = webSearchBackends.find((item) => item.provider === 'gemini');
    setMaxUses(String(backend?.maxUsesDefault ?? 5));
  }, [webSearchBackends]);

  useEffect(() => {
    void http
      .getPlatformWebSearchKey()
      .then(setPlatformSearchKey)
      .catch((error) => onToast({ tone: 'error', message: describeError(error, translate) }));
  }, [http, onToast, translate]);

  async function savePlatformSearchKey(event: FormEvent) {
    event.preventDefault();
    if (!searchApiKey.trim()) return;
    setBusy(true);
    try {
      const next = await http.setPlatformWebSearchKey({ apiKey: searchApiKey.trim() });
      setPlatformSearchKey(next);
      setSearchApiKey('');
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setBusy(false);
    }
  }

  async function toggleTenantBackend() {
    if (!tenantId.trim()) return;
    const parsed = maxUses.trim() === '' ? Number.NaN : Number(maxUses);
    setBusy(true);
    try {
      await http.setWebSearchBackend({
        tenantId: tenantId.trim(),
        provider: 'gemini',
        status: geminiSearchBackend?.status === 'active' ? 'disabled' : 'active',
        ...(Number.isFinite(parsed) ? { maxUsesDefault: parsed } : {}),
      });
      await onChanged();
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translate) });
    } finally {
      setBusy(false);
    }
  }

  const canConfigureTenant = Boolean(tenantId.trim() && platformSearchKey.hasCredential);
  return (
    <>
      <form className="panel form-grid" onSubmit={savePlatformSearchKey}>
        <h2>{t('providerKeys.platformSearchKey')}</h2>
        <p className="screen-help">{t('providerKeys.platformSearchKeyHelp')}</p>
        <span className={platformSearchKey.hasCredential ? 'badge active' : 'badge'}>
          {platformSearchKey.hasCredential ? t('common.set') : t('common.empty')}
        </span>
        <FormField
          label={t('providerKeys.geminiSearchApiKey')}
          type="password"
          value={searchApiKey}
          onChange={(event) => setSearchApiKey(event.target.value)}
        />
        <button disabled={!searchApiKey.trim() || busy}>{t('providerKeys.saveSearchKey')}</button>
      </form>
      <section className="panel form-grid">
        <h2>{t('providerKeys.tenantWebSearch')}</h2>
        <p className="screen-help">{t('providerKeys.tenantWebSearchHelp')}</p>
        <div className="credential-row">
          <div className="credential-info">
            <div className="credential-head">
              <strong>{t('providerKeys.geminiGrounding')}</strong>
              <span className={geminiSearchBackend?.status === 'active' ? 'badge active' : 'badge'}>
                {geminiSearchBackend?.status ?? 'disabled'}
              </span>
            </div>
            {!platformSearchKey.hasCredential ? (
              <small>{t('providerKeys.platformSearchKeyRequired')}</small>
            ) : null}
          </div>
          <div className="credential-actions">
            <label className="inline-field">
              {t('providerKeys.maxUses')}
              <input
                type="number"
                min={1}
                max={20}
                value={maxUses}
                disabled={!canConfigureTenant || busy}
                onChange={(event) => setMaxUses(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="secondary"
              disabled={!canConfigureTenant || busy}
              onClick={() => void toggleTenantBackend()}
            >
              {geminiSearchBackend?.status === 'active'
                ? t('providerKeys.disableSearch')
                : t('providerKeys.enableSearch')}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
